import { Module, Action, Mutation } from 'vuex-module-decorators'
import { filter, find } from 'lodash'
import {
  defaultBirthdayJapaneseSymbol,
  defaultSystemCode,
  GetSystemCodeRequest,
  SystemCode,
  systemCodeApi
} from '@/api'
import BaseStore from '@/store/BaseStore'
import CODE_KEY from '@/config/code_key'
import { authUserStore } from '@/utils/store-accessor'
import { SchoolGradeCode, UserMailContactType } from '@/types'

@Module({
  name: 'SystemCodeStore',
  namespaced: true,
  stateFactory: true
})
export default class SystemCodeStore extends BaseStore {
  items: SystemCode[] = []

  get allItems(): SystemCode[] {
    return this.items
  }

  get filterByCodeKey(): (codeKey: string) => SystemCode[] {
    return codeKey => {
      return filter<SystemCode>(this.items, { codeKey })
    }
  }

  get findByCodeKeyAndCode(): (
    codeKey: string,
    code: string
  ) => SystemCode | undefined {
    return (codeKey, code) => {
      return find<SystemCode>(this.filterByCodeKey(codeKey), {
        code
      })
    }
  }

  get getLibraryBranch(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.LIBRARY_BRANCH.KEY)
  }

  get getPlaceCode(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.PROCESSING_PLACE.KEY)
  }

  get getCollectionMemo(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.COLLECTION_MEMO.KEY)
  }

  get exclusionCodesByWorkMemo(): string[] {
    return this.getCollectionMemo
      .filter(systemCode => systemCode.option1 !== '1')
      .map(systemCode => systemCode.code)
  }
  get exclusionCodesByDisplayMemo(): string[] {
    return this.getCollectionMemo
      .filter(systemCode => systemCode.option2 !== '1')
      .map(systemCode => systemCode.code)
  }

  get exclusionCodesByLocalMemo(): string[] {
    return this.getCollectionMemo
      .filter(systemCode => systemCode.option3 !== '1')
      .map(systemCode => systemCode.code)
  }

  get getOrderMemo(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.COLLECTION_MEMO.KEY)
  }

  get getReserveCancelReason(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.RESERVE_CANCEL_REASON.KEY)
  }

  get getLendOutCollectionLostExpulsionType(): SystemCode[] {
    return this.filterByCodeKey(
      CODE_KEY.LEND_OUT_COLLECTION_LOST_EXPULSION_TYPE.KEY
    )
  }

  get getForwardingRequestLevel(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.FORWARDING_REQUEST_LEVEL.KEY)
  }

  get getReferenceResearchType(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.REFERENCE_RESEARCH_TYPE.KEY)
  }

  get getReferenceContentType(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.REFERENCE_CONTENT_TYPE.KEY)
  }

  get getReferenceQuestionerType(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.REFERENCE_QUESTIONER_TYPE.KEY)
  }

  get getReferenceAnswerStage(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.REFERENCE_ANSWER_STAGE.KEY)
  }

  get getReferencePublicLevel(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.REFERENCE_PUBLIC_LEVEL.KEY)
  }

  get getDbRegistrationAvailability(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.DB_REGISTRATION_AVAILABILITY.KEY)
  }

  get getGender(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.GENDER.KEY)
  }

  get getTrcCustomerCodeNameSetting(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.TRC_CUSTOMER_CODE_NAME_SETTING.KEY)
  }

  get getJapaneseCalendarTypeWithAD(): SystemCode[] {
    const types: SystemCode[] = this.filterByCodeKey(
      CODE_KEY.JAPANESE_CALENDAR_TYPE.KEY
    )
    const codeKeys: SystemCode[] = [defaultBirthdayJapaneseSymbol()]
    CODE_KEY.JAPANESE_CALENDAR_SORT.forEach(value => {
      const code = types.find(item => item.code === value)
      if (code !== undefined) {
        codeKeys.push(code)
      }
    })
    return codeKeys
  }

  get getUserType(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.USER_TYPE.KEY)
  }

  get getWorkType(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.WORKING_TYPE.KEY)
  }

  get getRemindExclude(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.REMIND_EXCLUDE.KEY)
  }

  get getUserStatus(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.USER_STATUS.KEY)
  }

  get getClassification(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.CLASSIFICATION.KEY)
  }

  get getOnlineInspectionList(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.ONLINE_INSPECTION_LIST.KEY)
  }

  get getSchoolGradeCode(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.SCHOOL_GRADE_CODE.KEY)
  }

  get getSchoolClassCode(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.SCHOOL_CLASS_CODE.KEY)
  }

  get isDisplayReturnPageLendInfo(): boolean {
    return this.getPlaceCode.some(
      systemCode =>
        systemCode.code === authUserStore.authUser.placeCode.code &&
        systemCode.option3 !== '1'
    )
  }

  get isClearReturnPageHandlingType(): boolean {
    return this.getPlaceCode.some(
      systemCode =>
        systemCode.code === authUserStore.authUser.placeCode.code &&
        systemCode.option4 !== '1'
    )
  }

  get isClearReturnPageShelfType(): boolean {
    return this.getPlaceCode.some(
      systemCode =>
        systemCode.code === authUserStore.authUser.placeCode.code &&
        systemCode.option5 !== '1'
    )
  }

  get getUserMailContactType(): SystemCode[] {
    return this.filterByCodeKey(CODE_KEY.USER_MAIL_CONTACT_TYPE.KEY)
  }

  get defaultMailContactReserve(): boolean {
    const item = this.getUserMailContactType.find(
      systemCode => systemCode.code === UserMailContactType.reserve
    )

    return item?.option1 === '1'
  }

  get defaultMailContactNewArrival(): boolean {
    const item = this.getUserMailContactType.find(
      systemCode => systemCode.code === UserMailContactType.newArrival
    )

    return item?.option1 === '1'
  }

  get defaultMailContactRemind(): boolean {
    const item = this.getUserMailContactType.find(
      systemCode => systemCode.code === UserMailContactType.remind
    )

    return item?.option1 === '1'
  }

  get defaultMailContactNotice(): boolean {
    const item = this.getUserMailContactType.find(
      systemCode => systemCode.code === UserMailContactType.notice
    )

    return item?.option1 === '1'
  }

  get defaultMailContactEvent(): boolean {
    const item = this.getUserMailContactType.find(
      systemCode => systemCode.code === UserMailContactType.event
    )

    return item?.option1 === '1'
  }

  get maxStudentGrade(): SystemCode {
    const systemCode: SystemCode | undefined = this.getSchoolGradeCode
      .filter(student => student.code >= '1' && student.code <= '6')
      .reduce(
        (max: undefined | SystemCode, systemCode: SystemCode) =>
          max === undefined || systemCode.code > max.code ? systemCode : max,
        undefined
      )

    return systemCode ?? defaultSystemCode()
  }

  // 「コード値と◯年生の数値が一致している」「コード値は数値文字列」という前提で処理をしている
  // その為、システムコードを書き換えられると正しく取得ができなくなる為注意
  get findOneRankUpStudentGrade(): (
    studentGradeCode: string
  ) => SystemCode | undefined {
    return (studentGradeCode: string) => {
      // 学年が最大の場合は、卒業生を表示する
      if (studentGradeCode === this.maxStudentGrade.code) {
        return this.getSchoolGradeCode.find(
          systemCode => systemCode.code === SchoolGradeCode.Graduation
        )
      }

      // ファイルから年組を設定していない利用者を取得出来る為、その場合は１年生を表示する
      return this.getSchoolGradeCode.find(
        systemCode =>
          systemCode.code === String(Number(studentGradeCode) + 1) ||
          (studentGradeCode === '' &&
            systemCode.code === SchoolGradeCode.FirstGrade)
      )
    }
  }

  @Mutation
  public setItems(systemCodes: SystemCode[] = []) {
    this.items = systemCodes
  }

  @Mutation
  public mergeItems(systemCodes: SystemCode[] = []) {
    const addKeys: string[] = []
    systemCodes.forEach(item => {
      if (!addKeys.includes(item.codeKey)) addKeys.push(item.codeKey)
    })
    this.items = this.items.filter(item => {
      if (!addKeys.includes(item.codeKey)) {
        return item
      }
    })
    systemCodes.forEach(item => {
      this.items.push(item)
    })
  }

  @Action({ rawError: true })
  public async fetchAllByCodeKeys(codeKeys: string[]) {
    if (codeKeys.length === 0) {
      this.setItems([])
      return
    }

    const systemCodeRequest: GetSystemCodeRequest = {
      codeKey: '',
      codeKeys,
      code: ''
    }

    const systemCodes = await systemCodeApi.getSystemCodes(systemCodeRequest)

    this.setItems(systemCodes)
  }

  @Action({ rawError: true })
  public async fetchAllAndMergeByCodeKeys(codeKeys: string[]) {
    if (codeKeys.length === 0) {
      return
    }

    const systemCodeRequest: GetSystemCodeRequest = {
      codeKey: '',
      codeKeys,
      code: ''
    }

    const systemCodes = await systemCodeApi.getSystemCodes(systemCodeRequest)

    this.mergeItems(systemCodes)
  }
}
